"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePicker = void 0;
const class_variance_authority_1 = require("class-variance-authority");
const date_fns_1 = require("date-fns");
const react_1 = __importStar(require("react"));
const react_day_picker_1 = require("react-day-picker");
const pi_1 = require("react-icons/pi");
const utils_1 = require("../../lib/utils");
const Button_1 = require("../Button/Button");
const IconButton_1 = require("../Button/IconButton");
const Calendar_1 = require("../Calendar/Calendar");
const Popover_1 = require("../Popover/Popover");
const readOnlyStyles = "read-only:tw-bg-primaryDim-25 read-only:focus-visible:tw-border-neutral-100 read-only:focus:tw-border-transparent read-only:focus:tw-ring-0";
const lockedStyles = "read-only:tw-bg-primary-25 read-only:focus-visible:tw-border-neutral-100 read-only:focus:tw-border-transparent read-only:focus:tw-ring-0";
const datePickerInputVariants = (0, class_variance_authority_1.cva)([
    "tw-inline-flex tw-w-full tw-px-3 tw-items-center tw-rounded-md tw-border tw-border-solid tw-border-neutral-100 tw-transition-colors",
    "tw-text-sm tw-font-semibold tw-font-sans",
    "focus-visible:tw-ring-ring focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-border-primary-300",
], {
    variants: {
        size: {
            sm: "tw-h-7",
            md: "tw-h-8",
            lg: "tw-h-9",
        },
    },
    defaultVariants: {
        size: "lg",
    },
});
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
];
const MonthSelect = ({ selected, onSelectMonth, }) => {
    const [year, setYear] = (0, react_1.useState)(Number(selected.getFullYear()));
    return (react_1.default.createElement("div", { className: "tw-w-auto tw-p-5" },
        react_1.default.createElement("div", { className: "tw-flex tw-flex-row tw-items-center tw-justify-between tw-px-1" },
            react_1.default.createElement(IconButton_1.IconButton, { size: "lg", color: "transparent", Icon: pi_1.PiCaretLeftBold, onClick: () => setYear((value) => Number(value) - 1) }),
            react_1.default.createElement("span", { className: "tw-text-md tw-font-semibold tw-text-neutral-400" }, year),
            react_1.default.createElement(IconButton_1.IconButton, { size: "lg", color: "transparent", Icon: pi_1.PiCaretRightBold, onClick: () => setYear((value) => Number(value) + 1) })),
        react_1.default.createElement("div", { className: "tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-1" }, MONTHS.map((month, index) => (react_1.default.createElement(Button_1.Button, { color: selected.getMonth() === index &&
                Number(selected.getFullYear()) === year
                ? "secondary"
                : "grey", onClick: () => onSelectMonth(new Date(selected.setFullYear(year, index))), className: "tw-flex tw-h-8 tw-w-[5.5rem] tw-items-center tw-justify-center tw-rounded", key: month }, month))))));
};
const formatDate = (calendarProps, dateFormat) => {
    if (!calendarProps.selected) {
        return;
    }
    if ((0, react_day_picker_1.isDayPickerSingle)(calendarProps)) {
        return (0, date_fns_1.format)(calendarProps.selected, dateFormat);
    }
    if ((0, react_day_picker_1.isDayPickerMultiple)(calendarProps)) {
        return calendarProps.selected.map((d) => (0, date_fns_1.format)(d, dateFormat)).join(", ");
    }
    if ((0, react_day_picker_1.isDayPickerRange)(calendarProps)) {
        const { from, to } = calendarProps.selected;
        if (!from) {
            return;
        }
        return `${(0, date_fns_1.format)(from, dateFormat)} - ${to ? (0, date_fns_1.format)(to, dateFormat) : ""}`;
    }
};
const getSelectedDate = (calendarProps) => {
    if ((0, react_day_picker_1.isDayPickerSingle)(calendarProps)) {
        return calendarProps.selected;
    }
    if ((0, react_day_picker_1.isDayPickerMultiple)(calendarProps) && calendarProps.selected?.length) {
        return calendarProps.selected[0];
    }
    if ((0, react_day_picker_1.isDayPickerRange)(calendarProps)) {
        return calendarProps.selected?.from;
    }
    return;
};
const DatePicker = (0, react_1.forwardRef)(({ size, locked, readOnly, placeholder = "- Add Date -", calendarProps, 
// Since we are in the U.S., we default to MM/dd/yyyy
dateFormat = "MM/dd/yyyy", className, }, ref) => {
    const disabled = locked || readOnly;
    const selectedDate = getSelectedDate(calendarProps);
    const [open, setOpen] = (0, react_1.useState)(false);
    const [month, setMonth] = (0, react_1.useState)(selectedDate);
    const [showMonthSelect, setShowMonthSelect] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        if (open) {
            setShowMonthSelect(false);
            setMonth(selectedDate);
        }
    }, [open]);
    const handleTodayClick = (event) => {
        if (!calendarProps.onSelect) {
            return;
        }
        setShowMonthSelect(false);
        if ((0, react_day_picker_1.isDayPickerSingle)(calendarProps)) {
            calendarProps.onSelect(new Date(), new Date(), {}, event);
            setMonth(new Date());
        }
        if ((0, react_day_picker_1.isDayPickerMultiple)(calendarProps) && calendarProps.onSelect) {
            calendarProps.onSelect([new Date()], new Date(), {}, event);
            setMonth(new Date());
        }
        if ((0, react_day_picker_1.isDayPickerRange)(calendarProps) && calendarProps.onSelect) {
            calendarProps.onSelect({ from: new Date() }, new Date(), {}, event);
            setMonth(new Date());
        }
    };
    const handleSelectMonth = (selectedMonth) => {
        setMonth(selectedMonth);
        setShowMonthSelect(false);
    };
    return (react_1.default.createElement(Popover_1.Popover, { open: open, onOpenChange: setOpen },
        react_1.default.createElement(Popover_1.PopoverTrigger, { disabled: disabled, className: (0, utils_1.cn)({
                [lockedStyles]: locked,
                [readOnlyStyles]: readOnly,
                "tw-cursor-default": disabled,
            }, className), ref: ref, asChild: true },
            react_1.default.createElement("button", { "aria-expanded": open, className: (0, utils_1.cn)(datePickerInputVariants({ size }), {
                    "tw-border-neutral-100": !open,
                    "tw-border-primary-300": open,
                    "tw-text-neutral-200": !getSelectedDate(calendarProps),
                }) },
                react_1.default.createElement("div", { className: "tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" }, formatDate(calendarProps, dateFormat) || placeholder),
                !disabled && (react_1.default.createElement(pi_1.PiCalendarBlankBold, { className: (0, utils_1.cn)("tw-ml-auto tw-h-4 tw-w-4 tw-shrink-0 tw-text-neutral-500 tw-transition-transform", { "tw-text-neutral-200": disabled }) })))),
        react_1.default.createElement(Popover_1.PopoverContent, { className: "tw-w-auto tw-py-1", align: "start" },
            showMonthSelect ? (react_1.default.createElement(MonthSelect, { selected: selectedDate || new Date(), onSelectMonth: handleSelectMonth })) : (react_1.default.createElement(Calendar_1.Calendar, { initialFocus: true, month: month, onMonthChange: handleSelectMonth, onClickMonth: () => setShowMonthSelect(true), ...calendarProps })),
            react_1.default.createElement("div", { className: "tw-flex tw-w-full tw-items-center tw-justify-center tw-pb-3" },
                react_1.default.createElement(Button_1.Button, { onClick: handleTodayClick }, `Set ${showMonthSelect ? "This Month" : "Today"}`)))));
});
exports.DatePicker = DatePicker;
DatePicker.displayName = "DatePicker";
